import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
	Box,
	Button,
	Chip,
	Grid,
	IconButton,
	Link as MaterialLink,
	Theme,
	Tooltip,
	Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import {
	EXPOSURE_CLONE_FAILURE,
	EXPOSURE_CLONE_LABEL,
	EXPOSURE_CLONE_SUCCESS,
} from '../../../data/labelsAndMessages';
import { Exposure, ExposureCharacteristics } from '../../../types/policy';
import {
	getActiveCharacteristics,
	getEntityNameFrom_DescriptionField,
	getExposureConfig,
	getPolicyOverrideResourceTimestamp,
	isExposureActive,
	isPolicyEditable,
} from '../../../util/util';
import { FastForm } from '../../fastForm';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import AddExposureModal from './AddExposureModal';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
}

const useStyles = makeStyles((theme: Theme) => ({
	titleWrapper: {
		display: 'flex',
		alignItems: 'flex-end',
	},
	auxDataLink: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	removeIcon: {
		position: 'relative',
		top: theme.spacing(1),
	},
}));

const ExposureDetails = (props: Props) => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const navigate = useNavigate();
	const classes = useStyles();
	const { authData, policyData } = props;
	const {
		productConfig,
		selectedQuote,
		selectedExposure,
		policy,
		policyViewTimestamp,
		fields: form,
		saveFastForm,
		setLoading,
	} = policyData;
	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = getExposureConfig(productConfig, selectedExposure);
	const isActive = isExposureActive(
		selectedExposure,
		policy,
		policyViewTimestamp,
	);
	const overrideResourcesAsOfTimestamp = getPolicyOverrideResourceTimestamp(
		policy,
		selectedQuote,
	);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [removedExposure, setRemovedExposure] = useState(null);

	const isInEditMode =
		authData.hasPermission('quote.update') &&
		isPolicyEditable(policy, selectedQuote);

	const renderActions = () => {
		return (
			<AddExposureModal
				exposureConfigs={policyConfig.exposures}
				policyData={policyData}
				authData={authData}
			/>
		);
	};

	const removeExposure = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			await saveFastForm(getValues(), dirtyFields, reset, {
				removeExposure: { exposureLocator: selectedExposure.locator },
			});
			successToast('Successfully removed exposure');
			navigate(`../exposures`);
		} catch (error) {
			errorToast('Error removing exposure');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const renderRemoveExposureModal = () => {
		const activeCharacteristics = getActiveCharacteristics(
			removedExposure.characteristics,
		).pop() as ExposureCharacteristics;
		return (
			<ConfirmationModal
				entityName={
					removedExposure.exposureName ||
					getEntityNameFrom_DescriptionField(activeCharacteristics) ||
					removedExposure.name
				}
				entityType={t('exposure.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={() => removeExposure()}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const renderRemoveExposureIcon = () => {
		return (
			<Tooltip title={'Remove ' + t('exposure.singular.upper')}>
				<IconButton
					aria-label='Remove'
					className={classes.removeIcon}
					onClick={() => openConfirmationModal(selectedExposure)}
				>
					<DeleteIcon />
				</IconButton>
			</Tooltip>
		);
	};

	const openConfirmationModal = (exposure: Exposure) => {
		setIsConfirmModalOpen(true);
		setRemovedExposure(exposure);
	};

	const displayName = exposureConfig.displayName
		? exposureConfig.displayName
		: selectedExposure.name;

	const handleCloneExposureClick = async () => {
		try {
			setLoading(true);
			const updatedPolicyData = await policyData.cloneExposure(
				selectedExposure,
				getValues(),
				reset,
				dirtyFields,
			);
			const addedExposure = updatedPolicyData?.policy?.exposures?.pop();
			if (addedExposure) {
				successToast(EXPOSURE_CLONE_SUCCESS());
				navigate('exposures/' + addedExposure.displayId);
			}
		} catch (e) {
			errorToast(EXPOSURE_CLONE_FAILURE());
			if (await handleUnauthorizedResponse(e, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};
	return (
		<React.Fragment key={pathname}>
			<Box className={classes.titleWrapper}>
				<Box display={'flex'} alignItems={'flex-end'} flexGrow={1}>
					<Typography textTransform={'capitalize'}>{displayName}</Typography>
					{selectedQuote?.state === 'draft' && (
						<Button
							variant='text'
							size='small'
							startIcon={<ContentCopyIcon />}
							onClick={handleCloneExposureClick}
							sx={{
								ml: 1,
							}}
						>
							{EXPOSURE_CLONE_LABEL()}
						</Button>
					)}
				</Box>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink
						className={classes.auxDataLink}
						component={Link}
						to={`auxData`}
					>
						Aux Data
					</MaterialLink>
				)}
				{!isActive && (
					<Chip
						color='default'
						label='Inactive'
						icon={<ErrorOutlineRoundedIcon />}
					/>
				)}
				{isInEditMode && renderRemoveExposureIcon()}
				{isConfirmModalOpen && renderRemoveExposureModal()}
			</Box>
			<hr />
			<Grid container spacing={2}>
				<FastForm
					formName={selectedExposure.locator}
					form={form}
					configVersion={props.policyData.productConfig.configVersion}
					authData={props.authData}
					productName={props.policyData.productConfig.name}
					disabled={!isInEditMode}
					save={saveFastForm}
					setLoading={setLoading}
					effectiveDate={overrideResourcesAsOfTimestamp}
				/>
			</Grid>

			{isInEditMode && renderActions()}
		</React.Fragment>
	);
};

export default withPolicyContext<Props>(ExposureDetails);
